import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  AlertCircle,
  Download,
  FileText,
  MoreVertical,
} from 'lucide-react';
import ProviderLayout from '@/components/layout/ProviderLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import apiService from '@/services/api';

interface Invoice {
  id: number;
  invoice_number: string;
  booking_id: number;
  service: string;
  customer_name: string;
  amount: number;
  invoice_date: string;
  due_date: string;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  payment_received_date?: string;
}

const ProviderInvoicesPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [showModal, setShowModal] = useState(false);
  const hasCheckedAuth = useRef(false);

  useEffect(() => {
    if (!authLoading && hasCheckedAuth.current === false) {
      hasCheckedAuth.current = true;
      if (!user || user.role !== 'provider') {
        navigate('/provider/login');
      }
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (user?.id && !authLoading) {
      fetchInvoices();
    }
  }, [user?.id, authLoading, filterStatus]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const response = await apiService.get(`/provider/invoices`);
      // Backend returns: {success: true, data: {invoices: [...]}}
      const invoicesList = response?.data?.invoices || response?.invoices || [];
      if (Array.isArray(invoicesList)) {
        let filtered = invoicesList;
        if (filterStatus !== 'all') {
          filtered = filtered.filter((inv: Invoice) => inv.status === filterStatus);
        }
        setInvoices(filtered);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast({
        title: 'Error',
        description: 'Failed to load invoices',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async (invoiceId: number, invoiceNumber: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/provider/invoices/${invoiceId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error(`Download failed (${response.status})`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast({
        title: 'Error',
        description: 'Failed to download invoice',
        variant: 'destructive',
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'sent':
        return 'bg-blue-100 text-blue-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-600';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const totalAmount = invoices.reduce((sum, inv) => sum + inv.amount, 0);
  const paidAmount = invoices
    .filter(inv => inv.status === 'paid')
    .reduce((sum, inv) => sum + inv.amount, 0);
  const overdueAmount = invoices
    .filter(inv => inv.status === 'overdue')
    .reduce((sum, inv) => sum + inv.amount, 0);

  if (authLoading) {
    return (
      <ProviderLayout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Authenticating...</p>
          </div>
        </div>
      </ProviderLayout>
    );
  }

  if (!user || user.role !== 'provider') {
    return (
      <ProviderLayout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">Not authorized to access this page</p>
            <Button onClick={() => navigate('/provider/login')}>Go to Login</Button>
          </div>
        </div>
      </ProviderLayout>
    );
  }

  return (
    <ProviderLayout>
      <Helmet>
        <title>Invoices - VireWire Provider</title>
        <meta name="description" content="Manage your invoices and billing" />
      </Helmet>

      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <h1 className="text-3xl font-bold text-gray-900">Invoices</h1>
            <p className="text-gray-600">Manage and download your customer invoices.</p>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-6 space-y-2">
              <p className="text-xs font-bold text-gray-600 uppercase">Total Invoiced</p>
              <p className="text-3xl font-bold text-gray-900">£{totalAmount.toFixed(2)}</p>
              <p className="text-xs text-gray-500">From {invoices.length} invoices</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6 space-y-2">
              <p className="text-xs font-bold text-gray-600 uppercase">Amount Paid</p>
              <p className="text-3xl font-bold text-green-600">£{paidAmount.toFixed(2)}</p>
              <p className="text-xs text-gray-500">Successfully received</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6 space-y-2">
              <p className="text-xs font-bold text-gray-600 uppercase">Overdue Amount</p>
              <p className="text-3xl font-bold text-red-600">£{overdueAmount.toFixed(2)}</p>
              <p className="text-xs text-gray-500">Requires action</p>
            </CardContent>
          </Card>
        </div>

        {/* Filter Section */}
        <div className="flex gap-2 items-center">
          <span className="text-sm font-semibold text-gray-700">Filter:</span>
          {['all', 'draft', 'sent', 'paid', 'overdue'].map(status => (
            <Button
              key={status}
              variant={filterStatus === status ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterStatus(status)}
              className={filterStatus === status ? 'bg-blue-600' : ''}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Button>
          ))}
        </div>

        {/* Invoices Table */}
        <Card>
          <CardContent className="p-6">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : invoices.length === 0 ? (
              <div className="py-12 text-center text-gray-600">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p>No invoices found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">INVOICE NO.</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">SERVICE</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">CUSTOMER</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">AMOUNT</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">INVOICE DATE</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">DUE DATE</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">STATUS</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">ACTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => (
                      <tr key={invoice.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 text-gray-900 font-semibold">#{invoice.invoice_number}</td>
                        <td className="py-3 px-4 text-gray-900">{invoice.service}</td>
                        <td className="py-3 px-4 text-gray-600">{invoice.customer_name}</td>
                        <td className="py-3 px-4 font-bold text-gray-900">£{invoice.amount.toFixed(2)}</td>
                        <td className="py-3 px-4 text-gray-600">
                          {new Date(invoice.invoice_date).toLocaleDateString('en-GB')}
                        </td>
                        <td className="py-3 px-4 text-gray-600">
                          {new Date(invoice.due_date).toLocaleDateString('en-GB')}
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={getStatusColor(invoice.status)}>
                            {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-gray-600">
                          <button
                            onClick={() => {
                              setSelectedInvoice(invoice);
                              setShowModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-800 p-2 hover:bg-gray-100 rounded"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Invoice Details Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          {selectedInvoice && (
            <div className="space-y-6 pt-4">
              <DialogHeader>
                <DialogTitle>Invoice #{selectedInvoice.invoice_number}</DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                <div>
                  <p className="text-xs text-gray-600 font-semibold mb-1">SERVICE</p>
                  <p className="text-gray-900 font-semibold">{selectedInvoice.service}</p>
                </div>

                <div>
                  <p className="text-xs text-gray-600 font-semibold mb-1">CUSTOMER</p>
                  <p className="text-gray-900 font-semibold">{selectedInvoice.customer_name}</p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-600 font-semibold mb-1">AMOUNT</p>
                  <p className="text-3xl font-bold text-gray-900">£{selectedInvoice.amount.toFixed(2)}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-600 font-semibold mb-1">INVOICE DATE</p>
                    <p className="text-gray-900">
                      {new Date(selectedInvoice.invoice_date).toLocaleDateString('en-GB')}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 font-semibold mb-1">DUE DATE</p>
                    <p className="text-gray-900">
                      {new Date(selectedInvoice.due_date).toLocaleDateString('en-GB')}
                    </p>
                  </div>
                </div>

                <div>
                  <p className="text-xs text-gray-600 font-semibold mb-1">STATUS</p>
                  <Badge className={getStatusColor(selectedInvoice.status)}>
                    {selectedInvoice.status.charAt(0).toUpperCase() + selectedInvoice.status.slice(1)}
                  </Badge>
                </div>

                {selectedInvoice.payment_received_date && (
                  <div>
                    <p className="text-xs text-gray-600 font-semibold mb-1">PAYMENT RECEIVED</p>
                    <p className="text-gray-900">
                      {new Date(selectedInvoice.payment_received_date).toLocaleDateString('en-GB')}
                    </p>
                  </div>
                )}

                <Button
                  onClick={() => handleDownloadInvoice(selectedInvoice.id, selectedInvoice.invoice_number)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download Invoice
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </ProviderLayout>
  );
};

export default ProviderInvoicesPage;
